import { debounce } from 'lodash';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getReportData, getTotalCount } from '../../../../../api/reports';
import { Loader, Pagination, Table } from '../../../../../common/components';
import { useSetLoading } from '../../../../../common/hooks/appearance';
import { usePagination } from '../../../../../common/hooks/data';
import {
  fetchDashboardFilterData,
  pnFuneralTrustAgingDetailesTableColumns,
} from '../../../../../utils/constant';
import * as handlers from '../../../../../utils/handlers';
import DownloadModal from '../../../downloadModal/downloadModal';
import EmailModal from '../../../emailModal/emailModal';
import ItemEmptyTile from '../../../item-empty-tile/item-empty-tile';
import Toolbar from '../../../toolbar/toolbar';
import onDownloadClick from '../../onDownloadClickHandler';
import PNFuneralTrustAgingDetailedRow from './pnFuneralTrustAgingDetailedRow';

const PNFuneralTrustAgingDetailed = ({ tableData, date, totalResults }) => {
  const filterData = useSelector((state) => state.filters.dashboardMultiSelectFilters);
  const [pnfTrustDetails, setPNFTrustDetails] = useState([]);
  const { page, limit, setPage, setLimit } = usePagination();
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(false);
  const [applyFilters, setApplyFilters] = useState(false);
  const [slStartNumber, setSlStartNumber] = useState(null);
  const [totalCount, setTotalCount] = useState(null);
  const [initialRenderDone, setInitialRenderDone] = useState(false);

  useEffect(() => {
    if (!initialRenderDone) {
      setInitialRenderDone(true);
      return;
    }
    getUpdatedTableData('globalFilterChange');
  }, [filterData]);

  useEffect(() => {
    setSlStartNumber(page * limit - limit);
    if (applyFilters) {
      getUpdatedTableData('pagination');
    }
  }, [page, limit, applyFilters]);

  const toggleEmailModal = () => {
    if (pnfTrustDetails?.length) {
      setIsEmailModalOpen(!isEmailModalOpen);
    }
  };

  const toggleDownloadModal = () => {
    if (pnfTrustDetails?.length) {
      setIsDownloadModalOpen(!isDownloadModalOpen);
    }
  };

  const getUpdatedTableData = async (type = 'pagination', value = '') => {
    setLoading(true);
    setApplyFilters(false);

    let page2 = page;
    let limit2 = limit;
    if (type === 'search' || type === 'globalFilterChange') {
      page2 = 1;
      limit2 = 5;
    }

    try {
      const requestParams = {
        reportDate: date,
        ...fetchDashboardFilterData(filterData),
        timezone: moment.tz.guess(),
        page: page2,
        limit: limit2,
        ...((type === 'search' || searchText.length > 0) && {
          opiOrName: type === 'search' ? value : searchText,
        }),
      };
      const { data } = await getReportData('pnFuneralTrustAgingDetailedReport', requestParams);
      if (type === 'search' || type === 'globalFilterChange') {
        setPage(1);
        setLimit(5);
        const response = await getTotalCount('pnFuneralTrustAgingDetailedReport', {
          ...fetchDashboardFilterData(filterData),
          reportDate: date,
          opiOrName: type === 'search' ? value : searchText,
          timezone: moment.tz.guess(),
        });
        setTotalCount(response?.data?.data?.totalCount);
      }

      setPNFTrustDetails(data?.data?.items || []);

      setLoading(false);
    } catch (err) {
      setLoading(false);
      handlers.apiErrorHandler(err);
    }
  };

  const debounceApiCall = useCallback(
    debounce((val) => {
      getUpdatedTableData('search', val);
    }, 2000),
    [],
  );

  const onSerchInputChange = (event) => {
    const {
      target: { value },
    } = event;
    setSearchText(value);
    debounceApiCall(value);
  };

  useEffect(() => {
    setPNFTrustDetails(tableData);
    setTotalCount(totalResults);
    setPage(1);
    setLimit(5);
    setSearchText('');
  }, [tableData, totalResults, setPage, setLimit]);

  const LoaderComponent = () => {
    if (loading) {
      return <Loader />;
    }
    return <ItemEmptyTile />;
  };

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  return (
    <>
      <Toolbar
        title="PN Funeral Trust Detailed Aging"
        subTitle={`As on Date: ${moment(date).format('MMM DD,YYYY')}`}
        optionOneLabel="Email Report"
        optionOneIcon="MdOutlineEmail"
        onOptionOneClick={toggleEmailModal}
        optionTwoLabel="Download Report"
        optionTwoIcon="FiDownload"
        setSearchText={setSearchText}
        onOptionTwoClick={toggleDownloadModal}
        searchText={searchText}
        isSearchable
        onSerchInputChange={onSerchInputChange}
        isTableDataPresent={pnfTrustDetails?.length > 0}
        toolTipText="This report provides a summary of outstanding accounts receivable, categorizing them based on their aging periods."
      />
      {pnfTrustDetails && pnfTrustDetails?.length > 0 ? (
        <>
          <div className="table-resp-scroll">
            <Table
              headers={pnFuneralTrustAgingDetailesTableColumns}
              bordered
              responsive
              className="pn-funeral-aging-detailed-table"
            >
              <tbody>
                {pnfTrustDetails?.map((rowData, rowNumber) => (
                  <PNFuneralTrustAgingDetailedRow
                    key={rowNumber}
                    slNo={slStartNumber + rowNumber + 1}
                    rowData={rowData}
                    id={rowNumber}
                    nestedColumnsNumber={pnFuneralTrustAgingDetailesTableColumns?.length - 2}
                  />
                ))}
              </tbody>
            </Table>
            {loading && <Loader />}
          </div>

          <div className="pagination-araging-report">
            <Pagination
              totalResults={totalCount}
              page={page}
              limit={limit}
              setPage={(pageNo) => {
                setApplyFilters(true);
                setPage(pageNo);
              }}
              setLimit={(rowlimit) => {
                setApplyFilters(true);
                setLimit(rowlimit);
              }}
            />
          </div>
        </>
      ) : (
        <div className="loader-empty">
          <LoaderComponent />
        </div>
      )}
      <EmailModal
        isOpen={isEmailModalOpen}
        toggle={toggleEmailModal}
        reportType="pnFuneralTrustAgingDetailedReport"
        apiValues={{
          reportDate: date,
          ...(searchText && { opiOrName: searchText }),
        }}
        newFilters
      />
      <DownloadModal
        isOpen={isDownloadModalOpen}
        toggle={toggleDownloadModal}
        reportType="pnFuneralTrustAgingDetailedReport"
        urlParams={{
          reportDate: date,
          action: 'download',
          ...(searchText && { opiOrName: searchText }),
        }}
        onDownloadClick={onDownloadClick}
        willReceiveReportFileInEmail
        newFilters
      />
    </>
  );
};

PNFuneralTrustAgingDetailed.propTypes = {
  tableData: PropTypes.shape([]),
  date: PropTypes.string,
  totalResults: PropTypes.number,
};

PNFuneralTrustAgingDetailed.defaultProps = {
  tableData: [],
  date: '',
  totalResults: 0,
};
export default PNFuneralTrustAgingDetailed;
